/**
 * Which restoration actions to offer on a photograph, and what to say instead.
 *
 * Three actions live side by side on the same screen and only one of them
 * works today, so the whole value of this module is in *not* flattening them:
 *
 * | action | what it is | today |
 * |---|---|---|
 * | İyileştir | measured, deterministic corrections | works |
 * | Bulanıklığı Azalt | a learned deblur | no model anywhere |
 * | Çözünürlüğü Artır | a learned super-resolution | no model anywhere |
 *
 * ## Resampling is not super-resolution
 *
 * The deterministic pipeline already enlarges a small photograph with Lanczos,
 * and that is arithmetic: more pixels, not one more piece of information about
 * the object. It is part of İyileştir and it is never labelled "Çözünürlüğü
 * Artır", because a customer who reads that phrase expects detail to come back
 * and it does not. The backend records `real_detail_recovered: false` on every
 * one of those derivatives for the same reason.
 *
 * ## When a control is hidden and when it is greyed
 *
 * The convention this product already had, extended rather than replaced:
 *
 * * **Not applicable to this file** - hidden. A photograph with no measured
 *   blur has no deblur button. This is the older rule, and it exists because
 *   the enhance button used to appear on everything, run, fail, and report
 *   that there was nothing to fix: the product offering work it could not do.
 * * **Applicable here, unavailable on this installation** - shown, disabled,
 *   with the reason. Somebody whose blurry photograph *could* be helped by a
 *   model that this server has no GPU for is being told something useful, and
 *   hiding it would make the feature look like it was never built.
 * * **No such model anywhere** - hidden. Not a thing this installation is
 *   temporarily missing; a thing the product does not have. A permanent grey
 *   button is a promise.
 *
 * Each blocker maps to its own sentence because each calls for something
 * different from somebody different - a model that does not exist is not a
 * machine without a GPU, and neither is a licence nobody has read. The reasons
 * come from the server's capability route, so this file cannot drift into
 * claiming a capability the installation does not have.
 */

import type { MediaAsset, ModelCapabilities, ModelTaskName } from "@/api/types";
import { canEnhance, type MediaAnalysis } from "./mediaAnalysis";

export type RestorationKind = "enhance" | "deblur" | "super_resolution";

export interface RestorationAction {
  kind: RestorationKind;
  label: string;
  /** Whether the control does anything when pressed. */
  enabled: boolean;
  /**
   * Why it does not, in the customer's words. Null when it does. Present even
   * where the control is hidden, so a caller that chooses to show it greyed
   * has something true to put beside it.
   */
  reason: string | null;
  /** Whether to show the control at all. See the module docstring. */
  visible: boolean;
  /**
   * The factors this installation can perform, for an action that has any.
   * Empty for every other kind. Read from the server rather than hardcoded:
   * a machine holding only the 2x file must not be offered 4x.
   */
  scales: number[];
  /**
   * What the customer has to be told *before* pressing, when the output is
   * not a measurement. Null when there is nothing to warn about.
   */
  caveat: string | null;
}

/** The reason sentences, one per blocker code the server can send. */
const BLOCKER_TEXT: Record<string, string> = {
  no_model:
    "Bu özellik için henüz bir model yok. Hazır olduğunda burada görünecek.",
  weights_absent:
    "Modelin dosyaları bu sunucuda bulunmuyor. Sistem yöneticinizle görüşün.",
  // Distinct from `weights_absent`, which is the catalogue's own answer. This
  // one means the container was never provisioned - the same failure the face
  // pipeline shipped once, where code arrived and weights did not.
  weights_missing:
    "Modelin dosyaları bu sunucuya henüz kurulmadı. Sistem yöneticinizle görüşün.",
  licence_unreviewed:
    "Modelin lisansı henüz incelenmedi; incelenene kadar kullanıma açılmıyor.",
  licence_refused: "Modelin lisansı bu üründe kullanıma izin vermiyor.",
  hardware_cuda_absent:
    "Bu sunucuda uygun bir grafik işlemcisi yok. Model, GPU'lu bir sunucuda çalışabilir.",
  disabled: "Bu özellik sistem yöneticiniz tarafından kapatılmış.",
};

/** What the file itself says: the action would do nothing here. */
const NOT_APPLICABLE: Record<RestorationKind, string> = {
  enhance: "Bu dosyada ölçülmüş ve güvenle düzeltilebilen bir sorun yok.",
  deblur: "Bu fotoğrafta ölçülmüş bir bulanıklık yok.",
  super_resolution: "Bu fotoğrafın çözünürlüğü düşük olarak ölçülmedi.",
};

const LABELS: Record<RestorationKind, string> = {
  enhance: "İyileştir",
  deblur: "Bulanıklığı Azalt",
  super_resolution: "Çözünürlüğü Artır",
};

const TASK_OF: Record<Exclude<RestorationKind, "enhance">, ModelTaskName> = {
  deblur: "image_deblur",
  super_resolution: "image_super_resolution",
};

/**
 * The sharpness below which a blur is not recoverable by anything.
 *
 * The same floor the deterministic pipeline and the deblur adapter use. Three
 * copies of a threshold is two too many, and this one is pinned by a test that
 * names the other two.
 */
export const BLUR_RECOVERY_SHARPNESS_FLOOR = 0.18;

const codesOf = (analysis: MediaAnalysis): Set<string> =>
  analysis.state === "assessed"
    ? new Set((analysis.assessment.issues ?? []).map((issue) => issue.code))
    : new Set();

/** Whether a deblur model would claim this photograph, from its measurement. */
export function deblurWouldApply(analysis: MediaAnalysis): boolean {
  if (analysis.state !== "assessed") return false;
  const codes = codesOf(analysis);
  if (!codes.has("BLUR") && !codes.has("POSSIBLE_MOTION_BLUR")) return false;
  // Below the floor the frame is not soft, it is gone, and the honest remedy
  // is another photograph rather than a restoration that makes it look fixed.
  return analysis.assessment.sharpness_score >= BLUR_RECOVERY_SHARPNESS_FLOOR;
}

/** Whether a super-resolution model would claim this photograph. */
export function superResolutionWouldApply(analysis: MediaAnalysis): boolean {
  return analysis.state === "assessed" && codesOf(analysis).has("LOW_RESOLUTION");
}

function capabilityOf(
  capabilities: ModelCapabilities | null,
  task: ModelTaskName,
): {
  available: boolean;
  blockers: string[];
  scales?: number[];
  recovers_real_detail?: boolean | null;
} {
  const found = capabilities?.tasks.find((entry) => entry.task === task);
  // Not yet loaded, or a server that does not know the route: treated as
  // unavailable with no reason. A control that lights up while the answer is
  // still in flight is a control that can be pressed into a 404.
  return found ?? { available: false, blockers: [] };
}

export { GENERATED_DETAIL_CAVEAT };

/**
 * The sentence that has to sit beside a control whose output is invented.
 *
 * Not a footnote and not a tooltip: super resolution produces an image that
 * *looks* more detailed and is further from what the camera saw - measured at
 * 9 dB behind a plain resample, with edges two and a half times sharper than
 * the real scene. On a photographed line of small print it wrote letter shapes
 * that spell nothing. Somebody about to measure an object in that picture has
 * to know before they press, not after.
 */
const GENERATED_DETAIL_CAVEAT =
  "Bu işlem eksik detayı geri getirmez; yapay olarak üretir. Sonuç daha " +
  "net görünür ama fotoğrafın kendisinden uzaklaşır, bu yüzden ölçüm ve 3D " +
  "modelde kullanılmaz.";

function learnedAction(
  kind: Exclude<RestorationKind, "enhance">,
  applies: boolean,
  capabilities: ModelCapabilities | null,
): RestorationAction {
  const entry = capabilityOf(capabilities, TASK_OF[kind]);
  const { available, blockers } = entry;
  const missingEntirely = blockers.includes("no_model") || blockers.length === 0;
  // A task that says its output is not real detail carries the warning with
  // it, wherever it is drawn. Driven by the server's own answer rather than by
  // this file knowing which model is behind which button.
  const caveat = entry.recovers_real_detail === false ? GENERATED_DETAIL_CAVEAT : null;
  const scales = entry.scales ?? [];

  if (!available) {
    return {
      kind,
      label: LABELS[kind],
      enabled: false,
      reason: BLOCKER_TEXT[blockers[0] ?? "no_model"] ?? BLOCKER_TEXT.no_model!,
      // Greyed where the capability exists and is out of reach; hidden where
      // there is no such model, and hidden where this file has nothing for it
      // to do.
      visible: !missingEntirely && applies,
      scales: [],
      caveat,
    };
  }
  return {
    kind,
    label: LABELS[kind],
    // Available, applicable, and - for an action that picks a factor - only
    // where there is a factor to pick. Available with no scale is a
    // contradiction the UI must not have to resolve.
    enabled: applies && (kind !== "super_resolution" || scales.length > 0),
    reason: applies ? null : NOT_APPLICABLE[kind],
    visible: applies,
    scales,
    caveat,
  };
}

/**
 * Every restoration action for one photograph, in the order they are offered.
 *
 * The measured, deterministic one first: it is the one that works, and burying
 * it under two capabilities nobody has would be arranging the screen around
 * what the product wishes it could do.
 */
export function restorationActions(
  asset: MediaAsset,
  analysis: MediaAnalysis,
  capabilities: ModelCapabilities | null,
): RestorationAction[] {
  const photo = asset.media_type === "photo";
  const usable = photo && canEnhance(asset, analysis);
  const enhance: RestorationAction = {
    kind: "enhance",
    label: LABELS.enhance,
    enabled: usable,
    reason: usable ? null : NOT_APPLICABLE.enhance,
    // Hidden on a file it would do nothing to. This installation can always
    // *run* it, so there is no third state here - either the measurement found
    // something it can correct or there is nothing to offer.
    visible: usable,
    scales: [],
    // Nothing to warn about: every step it may apply is a measured correction,
    // and the ones without evidence are excluded from reconstruction by
    // `enhancement_policy` rather than by a sentence on a screen.
    caveat: null,
  };
  if (!photo) return [];
  return [
    enhance,
    learnedAction("deblur", deblurWouldApply(analysis), capabilities),
    learnedAction(
      "super_resolution",
      superResolutionWouldApply(analysis),
      capabilities,
    ),
  ];
}

/**
 * The files in a selection a given action would actually do something to.
 *
 * What turns "12 bulanık fotoğraf" on the analysis screen into a batch: the
 * eligible subset, computed from the same measurement the count came from, so
 * the batch cannot include a file the single-file path would refuse.
 */
export function eligibleFor(
  kind: RestorationKind,
  files: readonly { asset: MediaAsset; analysis: MediaAnalysis }[],
): string[] {
  const applies = (entry: { asset: MediaAsset; analysis: MediaAnalysis }): boolean => {
    if (entry.asset.media_type !== "photo") return false;
    if (kind === "enhance") return canEnhance(entry.asset, entry.analysis);
    if (kind === "deblur") return deblurWouldApply(entry.analysis);
    return superResolutionWouldApply(entry.analysis);
  };
  return files.filter(applies).map((entry) => entry.asset.id);
}
